import React, { useState, useEffect } from 'react'
import {
  TextField,
  List,
  Datagrid,
  FunctionField,
  useTranslate,
  Form,
  DateField,
  TextInput,
  required,
  useRecordContext,
  email,
  useNotify,
} from 'react-admin'
import { ShowCard, CompleteSideCard } from 'src/components/uiElements/cards/ShowCard'
import { FormattedTextFieldWithStatus } from 'src/components/pages/sales/reservation/helper/FormattedTextFieldWithStatus'
import { emailHistoryServices } from 'src/services/notification/email-history/emailHistory.service'
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead'
import UnsubscribeIcon from '@mui/icons-material/Unsubscribe'
import SaveIcon from '@mui/icons-material/Save'
import LoadingButton from '@mui/lab/LoadingButton'
import SendIcon from '@mui/icons-material/Send'
import EmailIcon from '@mui/icons-material/Email'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  Grid,
  Alert,
  AlertTitle,
} from '@mui/material'
import { CheckboxLabels } from 'src/components/uiElements/fields/CheckboxLabels'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { ShowListElement } from 'src/components/uiElements/CRUD/ShowListElement'
import { useFormContext, useWatch } from 'react-hook-form'
import { handleRegenerateNotification } from '../../helper/handleRegenerateNotification'
import { handleForwardEmail } from '../../helper/handleForwardEmail'
import { useCardId } from '../../context/CardIdContext'
import { useCardLoading } from '../../context/CardLoadingContext'
import {  TableCell, TableHead, TableRow } from '@mui/material'
import FileOpenIcon from '@mui/icons-material/FileOpen';
import { voucherServices } from 'src/services/reservation/voucher/voucher.service'
import { hasPermissionFunction } from 'src/helper/HasPermission'
import DownloadIcon from '@mui/icons-material/Download';

const EmailModal = ({ open, handleClose, body }) => {
  const translate = useTranslate()

  return (
    <div>
      <Dialog open={open} maxWidth='lg' fullWidth={true}>
        <DialogTitle>
          {translate('ra.message.email_sent_to')} {body?.mail_to}
        </DialogTitle>
        <DialogContent>
          {body?.html_body ? (
            <div dangerouslySetInnerHTML={{ __html: body.html_body }} />
          ) : (
            <div>{translate('ra.message.no_content_to_display')}</div>
          )}
          <DialogActions className='dialog-actions' style={{ marginTop: '20px' }}>
            <Button onClick={handleClose} variant='outlined' color='error'>
              {translate('ra.action.back')}
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const UseAddessee = ({ mail_to }) => {
  const { setValue } = useFormContext()
  const use_addressee = useWatch({ name: 'use_addressee' })

  useEffect(() => {
    if (use_addressee && Array.isArray(use_addressee) && use_addressee.length >= 1) {
      setValue('email_to', mail_to)
    } else {
      setValue('email_to', null)
    }
  }, [use_addressee])
}

const ForwardEmailModal = ({ open, handleClose, record, onSubmit, loadingForwardEmailModal }) => {
  const translate = useTranslate()

  const postSave = (data) => {
    onSubmit && onSubmit({ ...data, hash_item: record?.item, status: record?.status_item })
  }

  return (
    <div>
      <Dialog open={open} maxWidth='lg' fullWidth={true}>
        <DialogTitle>{translate('ra.title.send_email')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <ShowListElement
              label={translate(`ra.field.status`)}
              value={translate(`ra.status.${record?.status_item}`).includes('.') ? record?.status_item : translate(`ra.status.${record?.status_item}`)}
            />
            <ShowListElement label={translate(`ra.field.addressee`)} value={record?.mail_to} />
          </Grid>
          <Form id='forward_email' warnWhenUnsavedChanges onSubmit={postSave} record={record}>
            <UseAddessee mail_to={record?.mail_to} />
            <Grid container spacing={3}>
              <EditInputsWrap className='small' label={translate('ra.field.send_to')} required>
                <TextInput source='email_to' label='' fullWidth validate={[required(), email()]} />
              </EditInputsWrap>
              <EditInputsWrap
                className='small'
                label={translate('ra.action.forward_something', { something: 'destinatario' })}
              >
                <CheckboxLabels source={'use_addressee'} choices={[{ key: record?.mail_to }]} />
              </EditInputsWrap>
            </Grid>
          </Form>
        </DialogContent>
        <DialogActions className='dialog-actions'>
          <LoadingButton
            loading={loadingForwardEmailModal}
            variant='contained'
            startIcon={<SaveIcon />}
            form='forward_email'
            type='submit'
          >
            {translate('ra.action.save')}
          </LoadingButton>
          <Button onClick={handleClose} variant='outlined' color='error'>
            {translate('ra.action.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const RegenerateNotificationModal = ({
  open,
  item,
  loadingRegenerateNotificationModal,
  onSubmit,
  handleClose,
}) => {
  const translate = useTranslate()

  const postSave = (data) => {
    onSubmit && onSubmit({ email_to: data.email_to, hash_item: item?.hash_ })
  }

  return (
    <div>
      <Dialog open={open} maxWidth='lg' fullWidth={true}>
        <DialogTitle>{translate('ra.title.regenerate_notification')}</DialogTitle>
        <DialogContent>
          {item?.contact?.email && (
            <Grid container spacing={3}>
              <ShowListElement label={translate(`ra.field.contact`)} value={item?.contact?.email} />
            </Grid>
          )}
          <Form id='regenerate_notification' warnWhenUnsavedChanges onSubmit={postSave} record={{}}>
            <UseAddessee mail_to={item?.contact?.email || ''} />
            <Grid container spacing={3}>
              <EditInputsWrap className='small' label={translate('ra.field.send_to')} required>
                <TextInput source='email_to' label='' fullWidth validate={[required(), email()]} />
              </EditInputsWrap>
              {item?.contact?.email && (
                <EditInputsWrap
                  className='small'
                  label={translate('ra.action.forward_something', { something: 'contacto' })}
                >
                  <CheckboxLabels source={'use_addressee'} choices={[{ key: item?.contact?.email || '' }]} />
                </EditInputsWrap>
              )}
            </Grid>
          </Form>
        </DialogContent>
        <DialogActions className='dialog-actions'>
          <LoadingButton
            loading={loadingRegenerateNotificationModal}
            variant='contained'
            startIcon={<SaveIcon />}
            form='regenerate_notification'
            type='submit'
          >
            {translate('ra.action.save')}
          </LoadingButton>
          <Button onClick={handleClose} variant='outlined' color='error'>
            {translate('ra.action.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export const NotificationEmails = () => {
  const notify = useNotify()
  const item = useRecordContext()
  const translate = useTranslate()
  const { cardId } = useCardId()
  const { setCardLoading } = useCardLoading()
  const [emailHistory, setEmailhistory] = useState([])
  const [open, setOpen] = useState(false)
  const [emailHistoryHtml, setEmailHistoryHtml] = useState(null)
  const [emailHistoryHtmlLoading, setEmailHistoryHtmlLoading] = useState({})
  const [emailHistoryLoading, setEmailhistoryLoading] = useState(false)
  const [openForwardEmailModal, setOpenForwardEmailModal] = useState(false)
  const [loadingForwardEmailModal, setLoadingForwardEmailModal] = useState(false)
  const [openRegenerateNotificationModal, setOpenRegenerateNotificationModal] = useState(false)
  const [loadingRegenerateNotificationModal, setLoadingRegenerateNotificationModal] = useState(false)
  const [generateVoucherLoading, setGenerateVoucherLoading] = useState(false)

  useEffect(() => {
    if (item?.hash_) {
      getEmailHistory(item?.hash_)
    }
  }, [item])

  const setLoadingForEmailHistory = (emailId, isLoading) => {
    setEmailHistoryHtmlLoading((prevState) => ({
      ...prevState,
      [emailId]: isLoading,
    }))
  }

  const handleDownload = (fileUrl) => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadVoucher = async () =>{
    setGenerateVoucherLoading(true)
    await voucherServices.getVoucherByHash(item?.hash_).then(res=>{
      handleDownload(res.voucher_url)
    }).catch(err=>{
      if(err.response?.status === 404){
        notify('ra.notification.voucher_not_found', {type:'error'})
      } else {
        notify('ra.notification.voucher_error', {type:'error'})
      }
    }).finally(()=>{
      setGenerateVoucherLoading(false)
    })
  }

  const generateVoucher = async () =>{
    setGenerateVoucherLoading(true)
    await voucherServices.generateVoucher(item?.hash_).then(res=>{
      if(res.voucher_url)
        handleDownload(res.voucher_url)
    }).catch(err=>{
      if(err.response?.status === 404){
        notify('ra.notification.voucher_not_found', {type:'error'})
      } else {
        notify('ra.notification.voucher_error', {type:'error'})
      }
    }).finally(()=>{
      setGenerateVoucherLoading(false)
    })
  }

  const getEmailHistory = async (item_hash) => {
    try {
      setEmailhistoryLoading(true)
      const params = {
        item: item_hash,
      }
      const response = await emailHistoryServices.getAllByParams(params)
      if (response) {
        setEmailhistory(response)
      } else {
        setEmailhistory([])
      }
    } catch (error) {
      setEmailhistory([])
    } finally {
      setEmailhistoryLoading(false)
    }
  }

  const errorStatus = (status) => {
    if (!status) {
      return (
        <div style={{ color: 'green' }}>
          <MarkEmailReadIcon />
        </div>
      )
    }
    return (
      <div style={{ color: 'red' }}>
        <UnsubscribeIcon />
      </div>
    )
  }

  const getEmailHistoryById = async (id) => {
    try {
      setLoadingForEmailHistory(id, true)
      const emailHistoryData = await emailHistoryServices.getById(id)
      if (emailHistoryData) {
        setEmailHistoryHtml(emailHistoryData)
      } else {
        setEmailHistoryHtml(null)
      }
    } catch (error) {
      console.log('getEmailHistoryById error', error)
      setEmailHistoryHtml(null)
    } finally {
      setLoadingForEmailHistory(id, false)
    }
  }

  const showEmailButton = (id) => {
    return (
      <LoadingButton
        loading={emailHistoryHtmlLoading?.[id]}
        variant='text'
        startIcon={<EmailIcon />}
        onClick={async () => {
          await getEmailHistoryById(id)
          setOpen(true)
        }}
      ></LoadingButton>
    )
  }

  const forwardEmailButton = (id) => {
    return (
      <LoadingButton
        loading={emailHistoryHtmlLoading?.[id]}
        variant='text'
        startIcon={<SendIcon />}
        onClick={async () => {
          await getEmailHistoryById(id)
          setOpenForwardEmailModal(true)
        }}
      ></LoadingButton>
    )
  }

  const handleConfirmForwardEmail = (data) => {
    setCardLoading(cardId, true)
    handleForwardEmail(
      data,
      cardId,
      setLoadingForwardEmailModal,
      setOpenForwardEmailModal,
      notify,
      setCardLoading,
      getEmailHistory
    )
    getEmailHistory(item?.hash_)
  }

  const handleConfirmRegenerateNotification = (data) => {
    setCardLoading(cardId, true)
    handleRegenerateNotification(
      data,
      cardId,
      setLoadingRegenerateNotificationModal,
      setOpenRegenerateNotificationModal,
      notify,
      setCardLoading,
      getEmailHistory
    )
    getEmailHistory(item?.hash_)
  }

    const CustomHeader = ({ children }) => (
      <TableHead>
        <TableRow>
          {React.Children.map(children, (child) =>
            React.isValidElement(child) ? (
              <TableCell key={child.props.source} style={{ textAlign: child.props.textAlign || 'left' }}>
                <span>{child.props.label}</span>
              </TableCell>
            ) : null
          )}
        </TableRow>
      </TableHead>
    )

  return (
    <>
      <ShowCard
        title={translate('ra.title.notification')}
        preActionButton={
            (hasPermissionFunction('voucher', 'generate') ||
            hasPermissionFunction('voucher', 'generate_full')) &&
            <div style={{display:'flex', flexDirection:'row'}}>
              <LoadingButton  
                startIcon={<DownloadIcon/>}
                onClick={downloadVoucher}
                loading={generateVoucherLoading}
                style={{marginRight:'10px', width: '200px'}}
                disabled = {
                  ((item.status != 'CONFIRMED' && item.status != 'INVOICED') ||
                  (item.payment_status != 'PAID' && item.payment_status != 'PARTIALLY_PAID' && item.payment_status != 'GUARANTEED')) &&
                  !hasPermissionFunction('voucher', 'generate_full')
                }
                variant='contained' 
                size='small'>
                  {translate('ra.action.download_voucher')}
              </LoadingButton>
              <LoadingButton  
                startIcon={<FileOpenIcon/>}
                onClick={generateVoucher}
                style={{marginRight:'10px', width: '200px'}}
                loading={generateVoucherLoading}
                disabled = {
                  ((item.status != 'CONFIRMED' && item.status != 'INVOICED') ||
                  (item.payment_status != 'PAID' && item.payment_status != 'PARTIALLY_PAID' && item.payment_status != 'GUARANTEED')) &&
                  !hasPermissionFunction('voucher', 'generate_full')
                }
                variant='contained' 
                size='small'>
                  {translate('ra.action.generate_voucher')}
              </LoadingButton>
            </div>
        }
        actionButtonJustify='flex-end'
        actionButtonText={translate('ra.action.regenerate_notification')}
        actionButtonStartIcon={<SendIcon />}
        actionButtonStyle={{ width: '200px' }}
        onClickButton={() => setOpenRegenerateNotificationModal(true)}
      >
        <CompleteSideCard>
          <Datagrid
            isLoading={emailHistoryLoading}
            data={emailHistory || []}
            bulkActionButtons={false}
            header={<CustomHeader />}
          >
            <FormattedTextFieldWithStatus
                label={translate('ra.field.status')}
                source='status_item'
                sortable={false}
              />
              <DateField label={translate('ra.field.date')} source='created_at' sortable={false} showTime />
            <TextField label={translate('ra.field.addressee')} source='mail_to' sortable={false} />
            <FunctionField
                label={translate('ra.field.email_sent')}
                render={(record) => errorStatus(record.error_status)}
              />
              <FunctionField
                label={translate('ra.action.see_something', { thing: 'email' })}
                render={(record) => showEmailButton(record?.id)}
              />
              <FunctionField
                label={translate('ra.action.forward')}
                render={(record) => forwardEmailButton(record?.id)}
              />
          </Datagrid>
        </CompleteSideCard>
      </ShowCard>

      <EmailModal open={open} handleClose={() => setOpen(false)} body={emailHistoryHtml} />

      <ForwardEmailModal
        open={openForwardEmailModal}
        handleClose={() => setOpenForwardEmailModal(false)}
        record={emailHistoryHtml}
        onSubmit={handleConfirmForwardEmail}
        loadingForwardEmailModal={loadingForwardEmailModal}
      />

      <RegenerateNotificationModal
        open={openRegenerateNotificationModal}
        item={item}
        loadingRegenerateNotificationModal={loadingRegenerateNotificationModal}
        onSubmit={handleConfirmRegenerateNotification}
        handleClose={() => setOpenRegenerateNotificationModal(false)}
      />
    </>
  )
}
